import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appDate, appTime } from '@/utils/appDateFormat';
import type { BrandingSettings } from '../types';

interface PayslipPreviewProps {
  settings: BrandingSettings;
}

/*
 * A facsimile of the PDF the server draws (Mowazaf.Server/Services/Payroll/PayslipBrandingService.cs:
 * BuildHeader, BuildContent, BuildFooter) with sample figures, so a branding change can be judged before it
 * is saved. Colours and the logo/net-pay rules mirror the server; keep them in step when that file changes.
 */

const DOC_TEXT = '#424242';
const DOC_LABEL = '#4b5563';
const DOC_RULE = '#e5e7eb';
const DEDUCTION_RED = '#dc2626';
const DEDUCTION_BG = '#fee2e2';

const SAMPLE = { gross: 5700, commission: 1628, deductions: 1385 };
/** The page is laid out at this width and scaled down, never reflowed, on narrower screens. */
const PAGE_WIDTH = 640;

const parseHex = (hex: string): [number, number, number] | null => {
  const h = (hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

/** LightenColorHex: mix towards white by `amount`; the server falls back to #f1f5f9 on a bad colour. */
const lighten = (hex: string, amount: number): string => {
  const rgb = parseHex(hex);
  if (!rgb) return '#f1f5f9';
  return `#${rgb.map((c) => Math.trunc(c + (255 - c) * amount).toString(16).padStart(2, '0')).join('')}`;
};

/** IsDarkColor: perceived luminance under 0.6. */
const isDark = (hex: string): boolean => {
  const rgb = parseHex(hex);
  if (!rgb) return false;
  return (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255 < 0.6;
};

const money = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PayslipPreview: React.FC<PayslipPreviewProps> = ({ settings }) => {
  const { t } = useTranslation();
  const { headerSettings, logoSettings, footerSettings, customization } = settings;

  const frameRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState({ scale: 1, height: 0 });
  useEffect(() => {
    const frame = frameRef.current;
    const page = pageRef.current;
    if (!frame || !page || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const scale = Math.min(1, frame.clientWidth / PAGE_WIDTH);
      setFit({ scale, height: page.offsetHeight * scale });
    };
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    observer.observe(page);
    measure();
    return () => observer.disconnect();
  }, []);

  const accent = customization.accentColor || '#059669';
  const headerBg = headerSettings.headerBackgroundColor || '#f9fafb';
  const netPayBg = lighten(headerBg, 0.3);
  const netPayDark = isDark(netPayBg);
  const footerColor = footerSettings.footerColor || '#6b7280';

  const now = new Date();
  const payMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const payPeriod = payMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // The PDF draws the logo only for these positions; any other value prints the text alone.
  const position = logoSettings.logoPosition;
  const logoAt =
    position === 'top-center' ? 'center'
      : ['top-left', 'left', 'header-left'].includes(position) ? 'left'
        : ['top-right', 'right', 'header-right'].includes(position) ? 'right'
          : null;
  const showLogo = logoSettings.hasLogo && !!logoSettings.logoUrl && logoAt !== null;
  const logo = showLogo ? (
    <div className="flex h-10 w-20 shrink-0 items-center justify-center">
      <img src={logoSettings.logoUrl ?? ''} alt={t('Logo')} className="max-h-10 max-w-[80px] object-contain" />
    </div>
  ) : null;
  const titleBlock = (
    <div className={logoAt === 'center' ? 'text-center' : ''}>
      <div className="text-base font-semibold leading-tight" style={{ color: headerSettings.headerColor }}>
        {headerSettings.companyName}
      </div>
      <div className="text-[10px] leading-snug" style={{ color: headerSettings.headerColor }}>
        {headerSettings.headerText}
      </div>
    </div>
  );

  const row = (label: string, value: string, opts?: { strong?: boolean; color?: string }) => (
    <div className="flex justify-between gap-3 px-2 py-2 text-[10px]" style={{ borderBottom: `1px solid ${DOC_RULE}` }}>
      <span style={{ color: opts?.strong ? DOC_TEXT : DOC_LABEL, fontWeight: opts?.strong ? 600 : 400 }}>{label}</span>
      <span className="font-semibold tabular-nums whitespace-nowrap" style={{ color: opts?.color ?? DOC_TEXT }}>{value}</span>
    </div>
  );
  const infoRow = (label: string, value: string) => (
    <div className="flex justify-between gap-3 text-[10px] leading-5">
      <span style={{ color: DOC_LABEL }}>{label}:</span>
      <span className="font-semibold text-end" style={{ color: DOC_TEXT }}>{value}</span>
    </div>
  );

  return (
    <section className="mb-6 rounded-lg bg-muted p-4" aria-labelledby="payslip-live-preview">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h4 id="payslip-live-preview" className="text-sm font-medium text-foreground">{t('Live Preview')}</h4>
        <span className="text-xs text-muted-foreground">{t('Sample figures, laid out as the PDF payslip')}</span>
      </div>

      <div ref={frameRef} dir="ltr" className="mx-auto w-full max-w-[640px] overflow-hidden" style={{ height: fit.height || undefined }}>
      <div
        ref={pageRef}
        className="relative overflow-hidden rounded border border-border bg-white px-10 py-8 shadow-sm"
        style={{
          width: PAGE_WIDTH,
          color: DOC_TEXT,
          fontFamily: 'Arial, Helvetica, sans-serif',
          transform: fit.scale < 1 ? `scale(${fit.scale})` : undefined,
          transformOrigin: 'top left',
        }}
      >
        {customization.showWatermark && customization.watermarkText && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-5xl font-bold text-gray-200 opacity-60" aria-hidden="true">
            {customization.watermarkText}
          </div>
        )}

        {/* Header: background, radius 8, padding 12, logo box 80 x 40 */}
        <div className="rounded-lg p-3" style={{ backgroundColor: headerBg }}>
          {logoAt === 'center' && showLogo ? (
            <div className="flex flex-col items-center gap-2">{logo}{titleBlock}</div>
          ) : logoAt === 'left' && showLogo ? (
            <div className="flex items-center gap-3">{logo}<div className="ps-3">{titleBlock}</div></div>
          ) : logoAt === 'right' && showLogo ? (
            <div className="flex items-center justify-between gap-3">{titleBlock}{logo}</div>
          ) : (
            titleBlock
          )}
        </div>
        <div className="mt-1.5 h-px bg-gray-300" />

        {/* Pay period + employee details */}
        <div className="mt-5 grid grid-cols-2 gap-6 rounded-lg border p-4" style={{ backgroundColor: '#f8fafc', borderColor: DOC_RULE }}>
          <div>
            <div className="mb-2 text-base font-bold" style={{ color: DOC_TEXT }}>{t('PAYSLIP')}</div>
            {infoRow(t('Pay Period'), payPeriod)}
            {infoRow(t('Payment Date'), appDate(payMonth))}
            {infoRow(t('Generated'), appDate(now))}
          </div>
          <div>
            <div className="mb-2 text-sm font-semibold" style={{ color: DOC_TEXT }}>{t('Employee Details')}</div>
            {infoRow(t('Full Name'), t('Sample employee'))}
            {infoRow(t('Department'), t('Sample department'))}
            {infoRow(t('Role'), t('Sample role'))}
          </div>
        </div>

        {/* Earnings */}
        <div className="mt-5 mb-2 text-sm font-semibold" style={{ color: accent }}>{t('Earnings')}</div>
        {row(t('Gross Salary'), `AED ${money(SAMPLE.gross)}`)}
        {row(t('Sales Commission'), `AED ${money(SAMPLE.commission)}`)}
        <div className="flex justify-between px-2 py-2 text-[10px] font-semibold" style={{ borderTop: '2px solid #d1d5db' }}>
          <span>{t('Total earnings')}</span>
          <span className="tabular-nums" style={{ color: accent }}>AED {money(SAMPLE.gross + SAMPLE.commission)}</span>
        </div>

        {/* Deductions */}
        <div className="mt-3 mb-2 text-sm font-semibold" style={{ color: DEDUCTION_RED }}>{t('Deductions')}</div>
        <div className="flex justify-between px-2 py-2 text-[10px]" style={{ backgroundColor: DEDUCTION_BG, color: DEDUCTION_RED }}>
          <span>{t('Unpaid leave')}</span>
          <span className="font-semibold tabular-nums">-AED {money(SAMPLE.deductions)}</span>
        </div>
        <div className="flex justify-between px-2 py-2 text-[10px] font-semibold" style={{ borderTop: '2px solid #d1d5db' }}>
          <span>{t('Total deductions')}</span>
          <span className="tabular-nums" style={{ color: DEDUCTION_RED }}>-AED {money(SAMPLE.deductions)}</span>
        </div>

        {/* Net pay: the header colour lightened 30%, text flips on luminance */}
        <div className="mt-5 rounded-lg p-4 text-center" style={{ backgroundColor: netPayBg }}>
          <div className="text-sm font-semibold" style={{ color: netPayDark ? '#ffffff' : '#1f2937' }}>{t('Net Pay')}</div>
          <div className="mt-1 text-2xl font-bold tabular-nums" style={{ color: netPayDark ? '#ffffff' : '#1f2937' }}>
            AED {money(SAMPLE.gross + SAMPLE.commission - SAMPLE.deductions)}
          </div>
          <div className="mt-1 text-[9px]" style={{ color: netPayDark ? '#e0e7ff' : '#6b7280' }}>
            {t('Amount to be credited to your bank account')}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 border-t border-gray-300 pt-2.5 text-center text-[9px] leading-relaxed" style={{ color: footerColor }}>
          <p>{footerSettings.footerText || 'Confidential - For Employee Use Only'}</p>
          {footerSettings.includeDisclaimer && footerSettings.disclaimerText && (
            <p className="text-[8px] italic">{footerSettings.disclaimerText}</p>
          )}
          {footerSettings.showHelpContact && footerSettings.helpContact && (
            <p>{t('For queries, contact')}: {footerSettings.helpContact}</p>
          )}
          <p>{t('Generated on')} {appDate(now)} {appTime(now)}</p>
        </div>
      </div>
      </div>
    </section>
  );
};

export default PayslipPreview;
